import { t } from 'i18next';
import { Lock, MoveLeft } from 'lucide-react';
import React from 'react';

import { kizeoMessaging } from '@/app/routes/embed/kizeo/messaging';
import {
  CardList,
  CardListItem,
  CardListItemSkeleton,
} from '@/components/ui/card-list';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PieceIcon } from '@/features/pieces/components/piece-icon';
import { piecesHooks } from '@/features/pieces/lib/pieces-hook';
import {
  StepMetadata,
  HandleSelectCallback,
} from '@/features/pieces/lib/types';
import { useIsPieceLocked } from '@/features/policy/lib/locked-pieces';
import { isNil } from '@activepieces/shared';

import { LockedPieceBanner } from './locked-piece-banner';

type StepsCardListProps = {
  selectedPieceMetadata: StepMetadata | undefined;
  handleSelect: HandleSelectCallback;
};

export const StepsCardList: React.FC<StepsCardListProps> = ({
  selectedPieceMetadata,
  handleSelect,
}) => {
  const { data: actionsOrTriggers, isLoading: isLoadingSelectedPieceMetadata } =
    piecesHooks.usePieceActionsOrTriggers({
      stepMetadata: selectedPieceMetadata,
    });

  const isSelectedPieceLocked = useIsPieceLocked(selectedPieceMetadata);

  if (
    (isNil(actionsOrTriggers) || !selectedPieceMetadata) &&
    !isLoadingSelectedPieceMetadata
  ) {
    return (
      <div className="flex flex-col gap-2 items-center justify-center h-full w-full">
        <MoveLeft className="w-10 h-10 rtl:rotate-180" />
        <div className="text-sm">{t('Please select a piece first')}</div>
      </div>
    );
  }
  return (
    <ScrollArea className="h-full" viewPortClassName="h-full">
      {isSelectedPieceLocked && selectedPieceMetadata && (
        <LockedPieceBanner
          pieceDisplayName={selectedPieceMetadata.displayName}
        />
      )}
      <CardList
        className="w-[350px] min-w-[350px] h-full gap-0"
        listClassName="gap-0"
      >
        {isLoadingSelectedPieceMetadata && (
          <CardListItemSkeleton numberOfCards={5} withCircle={false} />
        )}
        {!isLoadingSelectedPieceMetadata &&
          selectedPieceMetadata &&
          actionsOrTriggers && (
            <>
              {actionsOrTriggers.map((item) => (
                <CardListItem
                  className="p-2 w-full"
                  key={item.displayName}
                  onClick={() =>
                    isSelectedPieceLocked
                      ? kizeoMessaging.sendOpenBusinessModalMessage()
                      : handleSelect(selectedPieceMetadata, item)
                  }
                >
                  <div className="flex gap-3 items-center w-full">
                    <div>
                      <PieceIcon
                        logoUrl={selectedPieceMetadata.logoUrl}
                        displayName={selectedPieceMetadata.displayName}
                        showTooltip={false}
                        size={'sm'}
                      />
                    </div>
                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                      <div className="text-sm">{item.displayName}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.description}
                      </div>
                    </div>
                    {isSelectedPieceLocked && (
                      <Lock className="h-3 w-3 shrink-0 mr-2 text-muted-foreground" />
                    )}
                  </div>
                </CardListItem>
              ))}
            </>
          )}
      </CardList>
    </ScrollArea>
  );
};
